import { byDocumentOrder, isHidden, nearestHeading } from './domText';

const MIN_CHART_SIZE = 120;
const PIXEL_RATIO = 2;
const PLOTLY_SELECTOR = '.js-plotly-plot';

// Chart libraries style SVG through external stylesheets, which a serialized
// SVG rendered as an image cannot see, so the computed values are inlined.
const SVG_STYLE_PROPS = [
  'fill',
  'fill-opacity',
  'stroke',
  'stroke-width',
  'stroke-opacity',
  'stroke-dasharray',
  'opacity',
  'font-family',
  'font-size',
  'font-weight',
  'text-anchor',
  'dominant-baseline',
  'visibility',
  'display',
];

const sizeOf = (el) => {
  const rect = el.getBoundingClientRect();
  return { width: Math.round(rect.width), height: Math.round(rect.height) };
};

const isChartSized = (el) => {
  const { width, height } = sizeOf(el);
  return width >= MIN_CHART_SIZE && height >= MIN_CHART_SIZE;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Chart image could not be rendered'));
    img.src = src;
  });

const onWhite = (source, width, height, scale = 1) => {
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/png');
};

const captureCanvas = async (canvas) => ({
  dataUrl: onWhite(canvas, canvas.width, canvas.height),
  width: canvas.width,
  height: canvas.height,
});

const captureSvg = async (svg) => {
  const { width, height } = sizeOf(svg);
  const win = svg.ownerDocument.defaultView;
  const clone = svg.cloneNode(true);
  const sources = [svg, ...svg.querySelectorAll('*')];
  const targets = [clone, ...clone.querySelectorAll('*')];
  sources.forEach((src, i) => {
    const style = win.getComputedStyle(src);
    const inline = SVG_STYLE_PROPS.map(
      (p) => `${p}:${style.getPropertyValue(p)}`,
    ).join(';');
    targets[i].setAttribute('style', `${targets[i].getAttribute('style') || ''};${inline}`);
  });
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', width);
  clone.setAttribute('height', height);
  const markup = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
  try {
    const img = await loadImage(url);
    return {
      dataUrl: onWhite(img, width, height, PIXEL_RATIO),
      width: width * PIXEL_RATIO,
      height: height * PIXEL_RATIO,
    };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const capturePlotly = async (div) => {
  const { width, height } = sizeOf(div);
  const Plotly = div.ownerDocument.defaultView.Plotly;
  const dataUrl = await Plotly.toImage(div, {
    format: 'png',
    width,
    height,
    scale: PIXEL_RATIO,
  });
  return { dataUrl, width: width * PIXEL_RATIO, height: height * PIXEL_RATIO };
};

const findChartElements = (doc) => {
  const found = [];
  const hasPlotly = typeof doc.defaultView?.Plotly?.toImage === 'function';
  if (hasPlotly) {
    doc.querySelectorAll(PLOTLY_SELECTOR).forEach((el) => {
      found.push({ el, capture: capturePlotly });
    });
  }
  const outsidePlotly = (el) => !hasPlotly || !el.closest(PLOTLY_SELECTOR);
  doc.querySelectorAll('canvas').forEach((el) => {
    if (outsidePlotly(el)) found.push({ el, capture: captureCanvas });
  });
  doc.querySelectorAll('svg').forEach((el) => {
    if (outsidePlotly(el) && !el.parentElement?.closest('svg')) {
      found.push({ el, capture: captureSvg });
    }
  });
  return found
    .filter(({ el }) => !isHidden(el) && isChartSized(el))
    .sort((a, b) => byDocumentOrder(a.el, b.el));
};

/**
 * PNG snapshots of every chart rendered in a SEAL result document (Chart.js
 * canvases, Plotly plots, NVD3/D3 SVGs), in page order.
 *
 * @returns {Promise<{ title: string, dataUrl: string, width: number, height: number }[]>}
 */
export const captureCharts = async (doc) => {
  const charts = [];
  // Sequential: Plotly.toImage and image decoding are heavy on large reports.
  // eslint-disable-next-line no-restricted-syntax
  for (const { el, capture } of findChartElements(doc)) {
    // eslint-disable-next-line no-await-in-loop
    const image = await capture(el);
    charts.push({
      title: el.getAttribute('aria-label') || nearestHeading(el),
      ...image,
    });
  }
  return charts;
};
